import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout from './AgentLayout';
import axios from 'axios';
import { Search, Filter, Calendar, Plane, Eye, Download, LifeBuoy, FileText, XCircle, CheckCircle, Unlock, Loader2, Info, Clock } from 'lucide-react';
import RaiseTicketModal from '../../components/RaiseTicketModal';
import TripjackCancelModal from '../../components/TripjackCancelModal';
import RiyaCancelModal from '../../components/RiyaCancelModal';
import RiyaRescheduleModal from '../../components/RiyaRescheduleModal';
import RiyaSSRModal from '../../components/RiyaSSRModal';
import ConfirmHoldModal from '../../components/ConfirmHoldModal';
import { useAgentAuth } from '../../context/AgentAuthContext';
import { getUserFacingBookingId, getApiBookingRouteId } from '../../utils/userFacingBookingId';
import BookingStatusChips from '../../components/booking/BookingStatusChips';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INVOICE_UNAVAILABLE_REASONS = new Set([
  'agent_without_gstin_walkin',
  'invalid_agent_gstin',
  'legacy_no_markup_capture',
  'b2c_zero_taxable',
  'agent_not_found',
]);

const INVOICE_REASON_LABELS = {
  supplier_pending: 'Tax invoice pending — supplier confirmation',
  supplier_not_confirmed: 'Tax invoice pending — supplier not confirmed',
  payment_not_settled: 'Tax invoice pending — payment not settled',
  booking_on_hold: 'Tax invoice pending — booking on hold',
  agent_without_gstin_walkin: 'Tax invoice unavailable — complete your GST profile',
  invalid_agent_gstin: 'Tax invoice unavailable — invalid GSTIN on profile',
  legacy_no_markup_capture: 'Tax invoice unavailable — contact support',
  no_invoice: 'Tax invoice not yet available',
};

function getInvoiceUiState(booking) {
  if (booking?.gst_invoice_id) {
    return { type: 'available' };
  }
  const reason = booking?.invoice_reason || booking?.gst_invoice_deferred_reason;
  if (!reason) return null;
  const label = INVOICE_REASON_LABELS[reason] || INVOICE_REASON_LABELS.no_invoice;
  return {
    type: INVOICE_UNAVAILABLE_REASONS.has(reason) ? 'unavailable' : 'pending',
    reason,
    label,
  };
}

const AgentBookings = () => {
  const navigate = useNavigate();
  const { agent } = useAgentAuth() || {};
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [supportBooking, setSupportBooking] = useState(null);
  const [tjCancelBooking, setTjCancelBooking] = useState(null);
  const [riyaCancelBooking, setRiyaCancelBooking] = useState(null);
  const [riyaCancelMode, setRiyaCancelMode] = useState('cancel');
  const [riyaRescheduleBooking, setRiyaRescheduleBooking] = useState(null);
  const [riyaSSRBooking, setRiyaSSRBooking] = useState(null);
  const [confirmHoldBooking, setConfirmHoldBooking] = useState(null);
  const [releasingTripjackId, setReleasingTripjackId] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, [currentPage, statusFilter]);

  const fetchBookings = async () => {
    try {
      const params = {
        page: currentPage,
        limit: 20,
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search })
      };

      const response = await axios.get(`${API_URL}/api/agent/dashboard/bookings`, {
        withCredentials: true,
        params
      });

      setBookings(response.data.bookings);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookings();
  };

  const handleDownloadTicket = async (bookingId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/bookings/download-eticket/${bookingId}`,
        {
          responseType: 'blob',
          withCredentials: true
        }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eticket_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading ticket:', error);
      alert('Failed to download e-ticket. Please try again.');
    }
  };

  const handleDownloadInvoice = async (bookingId, invoiceNumber) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/agent/dashboard/bookings/${bookingId}/invoice/download`,
        {
          responseType: 'blob',
          withCredentials: true
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (invoiceNumber || bookingId).replace(/\//g, '_');
      link.setAttribute('download', `tax_invoice_${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading tax invoice:', error);
      const msg = error?.response?.status === 404
        ? 'Tax invoice not available for this booking. It may be a legacy booking or your GST profile was not complete at the time of booking. Please contact support if you need assistance.'
        : 'Failed to download tax invoice. Please try again.';
      alert(msg);
    }
  };

  const handleDownloadGenericInvoice = async (invoiceId, filenameBase) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/agent/dashboard/invoices/${invoiceId}/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(filenameBase || invoiceId).replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    }
  };

  const handleReleaseTripjackHold = async (booking) => {
    const tjId = booking.tripjack_booking_id || booking.supplier_booking_id;
    if (!tjId || !String(tjId).startsWith('TJ')) {
      alert('Hold cannot be released: supplier reference is missing. Please open booking details or contact support.');
      return;
    }
    if (!window.confirm(
      'Release this hold PNR? Seats return to inventory and the airline will mark the order UNCONFIRMED.'
    )) {
      return;
    }
    const routeKey = getApiBookingRouteId(booking) || (booking.id || booking.booking_id);
    setReleasingTripjackId(routeKey);
    try {
      const resp = await axios.post(
        `${API_URL}/api/tripjack/release-pnr`,
        { bookingId: tjId },
        { withCredentials: true }
      );
      if (resp.data?.success !== false) {
        alert(resp.data?.message || 'Hold released successfully.');
        await fetchBookings();
      } else {
        alert('Release did not complete. Please try again.');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d ? JSON.stringify(d) : err.message);
      alert(msg || 'Failed to release hold');
    } finally {
      setReleasingTripjackId(null);
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">My Bookings</h1>
          <p className="text-gray-600 mt-1">View and manage your booking history</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <form onSubmit={handleSearch} className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by PNR, Booking ID, or Passenger name..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </form>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="mb-3 flex gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-xs text-slate-700">
          <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" aria-hidden />
          <p>
            <span className="font-medium text-slate-800">Air ticket cancellations:</span>
            {' '}If you submitted a cancel and the list still shows &quot;confirmed&quot;, open <strong>Cancel</strong> again — we automatically resume tracking the in-flight request.
          </p>
        </div>

        {/* Bookings Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">PNR / Booking ID</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Passenger</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Route</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Booked On</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Travel Date</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Time to Travel</th>
                  <th className="text-right py-4 px-6 text-sm font-semibold text-gray-700">Amount</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length > 0 ? (
                  bookings.map((booking, index) => {
                    // Extract data with fallback for old bookings
                    const pnr = booking.pnr || booking.flight_data?.pnr || 'N/A';
                    const routeId = getApiBookingRouteId(booking);
                    const displayBookingRef = getUserFacingBookingId(booking);
                    const internalId = booking.id || booking.booking_id;
                    const passengerName = booking.passenger_name || 
                      (booking.passengers?.[0] ? `${booking.passengers[0].ti || ''} ${booking.passengers[0].fN || ''} ${booking.passengers[0].lN || ''}`.trim() : 'Passenger');
                    const route = booking.route || 
                      (booking.from_airport && booking.to_airport
                        ? `${booking.from_airport} → ${booking.to_airport}`
                        : null) ||
                      (booking.flight_data ? `${booking.flight_data.from || 'N/A'} → ${booking.flight_data.to || 'N/A'}` : 'N/A');

                    // Travel date fallback across schemas (nested + flat)
                    const travelDateRaw = booking.travel_date 
                      || booking.flight_date 
                      || booking.departure_date
                      || booking.departure_time
                      || booking.flight_data?.flightDate 
                      || booking.flight_data?.segments?.[0]?.dt;
                    const travelDate = travelDateRaw ? new Date(travelDateRaw) : null;
                    const travelDateDisplay = travelDate && !isNaN(travelDate) ? travelDate.toLocaleDateString() : '-';
                    
                    // Days to travel
                    let timeToTravel = '-';
                    let timeBadgeClass = 'bg-gray-100 text-gray-600';
                    if (travelDate && !isNaN(travelDate)) {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const td = new Date(travelDate);
                      td.setHours(0, 0, 0, 0);
                      const diffDays = Math.round((td - today) / (1000 * 60 * 60 * 24));
                      if (diffDays > 0) {
                        timeToTravel = `${diffDays} day${diffDays === 1 ? '' : 's'}`;
                        timeBadgeClass = diffDays <= 3 ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
                      } else if (diffDays === 0) {
                        timeToTravel = 'Today';
                        timeBadgeClass = 'bg-orange-100 text-orange-700';
                      } else {
                        timeToTravel = `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} ago`;
                        timeBadgeClass = 'bg-gray-100 text-gray-600';
                      }
                    }

                    const tjReleaseId = booking.tripjack_booking_id || booking.supplier_booking_id;
                    const srcLower = (booking.source || booking.flight_data?.source || '').toLowerCase();
                    const isTjRow = srcLower === 'tripjack' || (tjReleaseId && String(tjReleaseId).startsWith('TJ'));
                    const rowSt = (booking.status || '').toLowerCase();
                    const supOrder = String(booking.supplier_order_status || booking.tripjack_status || '').toUpperCase();
                    const showTjUnhold = isTjRow && tjReleaseId && String(tjReleaseId).startsWith('TJ')
                      && rowSt !== 'released' && supOrder !== 'UNCONFIRMED'
                      && !['CONFIRMED', 'SUCCESS', 'TICKETED'].includes(supOrder)
                      && !['confirmed', 'success', 'ticketed'].includes(rowSt)
                      && (
                        ['ON_HOLD', 'HOLD'].includes(supOrder)
                        || ['on_hold', 'hold'].includes(rowSt)
                        || (rowSt === 'pending' && booking.is_hold === true)
                      );

                    return (
                      <tr key={index} className="border-t hover:bg-gray-50">
                        <td className="py-4 px-6">
                          <p className="font-medium text-gray-800">{pnr}</p>
                          <p className="text-xs text-gray-600" title="ACF booking reference">{displayBookingRef}</p>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-800">
                          {passengerName}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {route}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-4 px-6 text-sm font-medium text-gray-800">
                          {travelDateDisplay}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${timeBadgeClass}`}>
                            {timeToTravel}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right font-semibold text-gray-800">
                          ₹{booking.total_amount?.toLocaleString()}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <BookingStatusChips booking={booking} />
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate(`/booking-details/${routeId}`)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Details"
                              data-testid={`agent-booking-view-${routeId}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadTicket(routeId)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Download E-Ticket"
                              data-testid={`agent-booking-eticket-${routeId}`}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            {(() => {
                              const inv = getInvoiceUiState(booking);
                              if (inv?.type === 'available') {
                                return (
                                  <button
                                    onClick={() => handleDownloadInvoice(routeId, booking.gst_invoice_number)}
                                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title={`Download Tax Invoice${booking.gst_invoice_number ? ` · ${booking.gst_invoice_number}` : ''}`}
                                    data-testid={`agent-booking-tax-invoice-${routeId}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>
                                );
                              }
                              if (inv?.type === 'pending') {
                                return (
                                  <span
                                    className="p-2 text-amber-600 inline-flex rounded-lg"
                                    title={inv.label}
                                    data-testid={`agent-booking-invoice-pending-${routeId}`}
                                  >
                                    <Clock className="w-4 h-4" />
                                  </span>
                                );
                              }
                              if (inv?.type === 'unavailable') {
                                return (
                                  <span
                                    className="p-2 text-gray-400 inline-flex rounded-lg"
                                    title={inv.label}
                                    data-testid={`agent-booking-invoice-unavailable-${routeId}`}
                                  >
                                    <FileText className="w-4 h-4 opacity-50" />
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            {booking.credit_note_id ? (
                              <button
                                onClick={() => handleDownloadGenericInvoice(booking.credit_note_id,
                                  `credit_note_${booking.credit_note_number || booking.credit_note_id}`)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title={`Download Credit Note${booking.credit_note_number ? ` · ${booking.credit_note_number}` : ''}`}
                                data-testid={`agent-booking-credit-note-${routeId}`}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                            ) : null}
                            {(booking.supplementary_invoices || []).map((si, idx) => (
                              <button
                                key={si.id || idx}
                                onClick={() => handleDownloadGenericInvoice(si.id, `supp_${si.number || si.id}`)}
                                className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title={`Download Supplementary${si.number ? ` · ${si.number}` : ''}${si.parent_type ? ` (${si.parent_type})` : ''}`}
                                data-testid={`agent-booking-supp-${routeId}-${idx}`}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                            ))}
                            <button
                              data-testid={`agent-need-help-${routeId}`}
                              onClick={() => setSupportBooking(booking)}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Raise support ticket"
                            >
                              <LifeBuoy className="w-4 h-4" />
                            </button>
                            {/* Sprint T1 — Cancel Tripjack-source confirmed bookings only.
                                Riya bookings also store their PNR in `supplier_booking_id`,
                                so we explicitly exclude them here. */}
                            {(booking.status === 'confirmed' || booking.status === 'success')
                              && (booking.source || '').toLowerCase() !== 'riya'
                              && !booking.riya_pnr
                              && (((booking.source || '').toLowerCase() === 'tripjack')
                                  || booking.tripjack_booking_id
                                  || booking.supplier_booking_id) && (
                              <button
                                data-testid={`agent-cancel-booking-${routeId}`}
                                onClick={() => setTjCancelBooking(booking)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Cancel booking"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            {/* Sprint A — Cancel Riya-source bookings (issued or held) */}
                            {(((booking.source || '').toLowerCase() === 'riya') || booking.riya_pnr)
                              && (booking.status || '').toLowerCase() !== 'cancelled' && (
                              <button
                                data-testid={`agent-riya-cancel-${routeId}`}
                                onClick={() => {
                                  const status = (booking.riya_status || booking.status || '').toLowerCase();
                                  const isHeld = ['on_hold', 'hold', 'pending'].includes(status);
                                  setRiyaCancelMode(isHeld ? 'hold-cancel' : 'cancel');
                                  setRiyaCancelBooking(booking);
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title={['on_hold', 'hold', 'pending'].includes((booking.riya_status || booking.status || '').toLowerCase()) ? 'Release Hold PNR' : 'Cancel booking'}
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            {/* Sprint C — Reschedule (Riya, confirmed) */}
                            {(((booking.source || '').toLowerCase() === 'riya') || booking.riya_pnr)
                              && ['confirmed', 'ticketed', 'success'].includes((booking.status || '').toLowerCase()) && (
                              <button
                                data-testid={`agent-riya-reschedule-${routeId}`}
                                onClick={() => setRiyaRescheduleBooking(booking)}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                title="Reschedule"
                              >
                                <Calendar className="w-4 h-4" />
                              </button>
                            )}
                            {/* Sprint C — Add Extras / SSR (Riya, confirmed) */}
                            {(((booking.source || '').toLowerCase() === 'riya') || booking.riya_pnr)
                              && ['confirmed', 'ticketed', 'success'].includes((booking.status || '').toLowerCase()) && (
                              <button
                                data-testid={`agent-riya-ssr-${routeId}`}
                                onClick={() => setRiyaSSRBooking(booking)}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Add extras (meals/baggage)"
                              >
                                <Plane className="w-4 h-4" />
                              </button>
                            )}
                            {/* Tripjack — release hold PNR (unhold) */}
                            {showTjUnhold && (
                              <button
                                type="button"
                                data-testid={`agent-tripjack-unhold-${routeId}`}
                                onClick={() => handleReleaseTripjackHold(booking)}
                                disabled={releasingTripjackId === (routeId || internalId)}
                                className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-40"
                                title="Release hold (unhold)"
                              >
                                {releasingTripjackId === (routeId || internalId) ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Unlock className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            {/* Sprint H1+H2 — Confirm Hold ticket */}
                            {['on_hold', 'hold', 'pending'].includes((booking.tripjack_status || booking.riya_status || booking.status || '').toLowerCase()) && (
                              <button
                                data-testid={`agent-confirm-hold-${routeId}`}
                                onClick={() => setConfirmHoldBooking(booking)}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Confirm Hold ticket"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="py-12 text-center">
                      <Plane className="mx-auto text-gray-300 mb-3" size={48} />
                      <p className="text-gray-500">No bookings found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t p-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      <RaiseTicketModal
        open={!!supportBooking}
        onClose={() => setSupportBooking(null)}
        booking={supportBooking || {}}
        defaultEmail={agent?.email || ''}
        defaultPhone={agent?.phone || ''}
        portal="agent"
        onCreated={() => { setSupportBooking(null); navigate('/agent/support-tickets'); }}
      />
      <TripjackCancelModal
        open={!!tjCancelBooking}
        onClose={() => setTjCancelBooking(null)}
        bookingId={getApiBookingRouteId(tjCancelBooking) || tjCancelBooking?.id || tjCancelBooking?.booking_id}
        onCancelled={() => { setTjCancelBooking(null); fetchBookings(); }}
      />
      <RiyaCancelModal
        open={!!riyaCancelBooking}
        onClose={() => setRiyaCancelBooking(null)}
        bookingId={getApiBookingRouteId(riyaCancelBooking) || riyaCancelBooking?.id || riyaCancelBooking?.booking_id}
        booking={riyaCancelBooking || {}}
        mode={riyaCancelMode}
        onCancelled={() => { setRiyaCancelBooking(null); fetchBookings(); }}
      />
      <RiyaRescheduleModal
        open={!!riyaRescheduleBooking}
        onClose={() => setRiyaRescheduleBooking(null)}
        booking={riyaRescheduleBooking || {}}
      />
      <RiyaSSRModal
        open={!!riyaSSRBooking}
        onClose={() => setRiyaSSRBooking(null)}
        booking={riyaSSRBooking || {}}
      />
      <ConfirmHoldModal
        open={!!confirmHoldBooking}
        onClose={() => setConfirmHoldBooking(null)}
        booking={confirmHoldBooking || {}}
        onConfirmed={() => { setConfirmHoldBooking(null); fetchBookings(); }}
      />
    </AgentLayout>
  );
};

export default AgentBookings;